import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { fmtDate, fmtTRY, ORDER_STATUS_COLORS } from "../../lib/format";

const SEGMENT_LABEL = { NEW: "Yeni", ACTIVE: "Aktif", RETURNING: "Sadık", INACTIVE: "Pasif" };

export default function CustomerDetailPage() {
  const { customerId } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/admin/customers/${customerId}`).then(({ data }) => setData(data)).catch((e) => toast.error(errMsg(e)));
  }, [customerId]);

  if (!data) return <div className="h-64 bg-[#181B20] rounded-2xl animate-pulse-soft" />;
  const { customer, orders, addresses, returns, invoices } = data;

  return (
    <div className="space-y-5" data-testid="admin-customer-detail">
      <Link to="/admin/musteriler" className="inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-white">
        <ArrowLeft size={15} /> Müşterilere dön
      </Link>

      <div className="bg-[#181B20] border border-[#272B33] rounded-2xl p-6 flex flex-wrap items-center gap-6">
        <div className="flex-1 min-w-[220px]">
          <h1 className="font-display font-extrabold text-2xl text-white">{customer.name}</h1>
          <p className="text-sm text-neutral-400 mt-1">{customer.email} {customer.phone ? `· ${customer.phone}` : ""}</p>
          <p className="text-xs text-neutral-500 mt-1">Kayıt: {fmtDate(customer.created_at)}</p>
        </div>
        <div className="flex gap-6 text-center">
          <div><p className="text-xl font-display font-extrabold text-white" data-testid="customer-order-count">{customer.order_count}</p><p className="text-[11px] text-neutral-500">Sipariş</p></div>
          <div><p className="text-xl font-display font-extrabold text-white" data-testid="customer-total-spent">{fmtTRY(customer.total_spent)}</p><p className="text-[11px] text-neutral-500">Toplam Harcama</p></div>
          <div><span className="text-[11px] font-bold px-3 py-1.5 rounded-full bg-blue-500/15 text-blue-400" data-testid="customer-segment">{SEGMENT_LABEL[customer.segment] || customer.segment || "—"}</span><p className="text-[11px] text-neutral-500 mt-1">Segment</p></div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-[#181B20] border border-[#272B33] rounded-2xl p-5">
          <p className="text-sm font-bold text-white mb-3">Siparişler ({orders.length})</p>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {orders.map((o) => (
              <div key={o.order_number} className="flex items-center justify-between text-sm border-b border-[#272B33]/50 pb-2">
                <div>
                  <p className="font-mono-ed text-blue-400 text-xs">{o.order_number}</p>
                  <p className="text-[11px] text-neutral-500">{fmtDate(o.created_at)}</p>
                </div>
                <div className="text-right">
                  <p className="text-white font-semibold">{fmtTRY(o.total)}</p>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${ORDER_STATUS_COLORS[o.status] || "bg-neutral-700 text-neutral-300"}`}>{o.status}</span>
                </div>
              </div>
            ))}
            {orders.length === 0 && <p className="text-xs text-neutral-500">Sipariş yok</p>}
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-[#181B20] border border-[#272B33] rounded-2xl p-5">
            <p className="text-sm font-bold text-white mb-3">Adresler ({addresses.length})</p>
            <div className="space-y-2">
              {addresses.map((a) => (
                <div key={a.address_id} className="text-xs text-neutral-400 border-b border-[#272B33]/50 pb-2">
                  <p className="text-white font-medium">{a.title || a.full_name}</p>
                  <p>{a.address_line}, {a.district}/{a.city}</p>
                </div>
              ))}
              {addresses.length === 0 && <p className="text-xs text-neutral-500">Kayıtlı adres yok</p>}
            </div>
          </div>
          <div className="bg-[#181B20] border border-[#272B33] rounded-2xl p-5">
            <p className="text-sm font-bold text-white mb-3">İade Geçmişi ({returns.length})</p>
            <div className="space-y-2">
              {returns.map((r) => (
                <div key={r.return_id} className="flex justify-between text-xs">
                  <span className="text-neutral-400">{r.order_number} · {r.reason}</span>
                  <span className="text-neutral-500">{r.status}</span>
                </div>
              ))}
              {returns.length === 0 && <p className="text-xs text-neutral-500">İade talebi yok</p>}
            </div>
          </div>
          <div className="bg-[#181B20] border border-[#272B33] rounded-2xl p-5">
            <p className="text-sm font-bold text-white mb-3">Faturalar ({invoices.length})</p>
            <div className="space-y-2">
              {invoices.slice(0, 8).map((inv) => (
                <div key={inv.invoice_id} className="flex justify-between text-xs">
                  <span className="text-neutral-400 font-mono-ed">{inv.invoice_number || inv.order_number}</span>
                  <span className="text-neutral-500">{inv.status}</span>
                </div>
              ))}
              {invoices.length === 0 && <p className="text-xs text-neutral-500">Fatura yok</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
